import { useState } from 'react';
import type { ReactNode } from 'react';

import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import { Chip, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FieldRow } from './FieldRow';

export interface ConfiguredSecretFieldProps {
  label: ReactNode;
  description?: ReactNode;
  /**
   * Whether a secret is currently stored, as a bare boolean — the channel adapters (Telegram/MAX)
   * return NO credential field at all any more, only this presence flag (plan-conversation-001;
   * the frozen `bot-config.v1.json` fixture). `undefined` when the BFF has no presence signal for
   * this particular secret (e.g. the webhook secret today) — renders no status chip rather than
   * guessing.
   */
  configured: boolean | undefined;
  /**
   * A masked preview of the stored secret (e.g. `****ab12`), when the caller's contract carries one
   * (the AI provider card's `credentialMasked` — design-chatbot-ai-provider-card.md §7.5). Renders
   * "Set · {{masked}}" instead of the bare "Set" chip. Omitted/`null` keeps the original two-state
   * chip — most callers' contracts carry no masked value at all.
   */
  masked?: string | null;
  /** The write-only replacement secret (controlled). Blank on save means "keep the current value". */
  secret: string;
  onSecretChange: (value: string) => void;
  disabled?: boolean;
  /** Optional secrets (e.g. the webhook secret) use softer copy — "Enter a new secret…". */
  optional?: boolean;
  vaultManaged?: boolean;
  /** DOM id on the input, for a caller that needs to focus it programmatically (e.g. the AI
   * provider card's provider-check panel "Enter a key" action). */
  id?: string;
}

/**
 * The `configured`-boolean sibling of {@link MaskedCredentialField}: renders a secret's presence
 * as a status chip and a write-only input to set/replace it, with **no masked value anywhere** —
 * the contract no longer carries one, so this component must never invent or display one either
 * (design-channel-connection-pages.md §5.2).
 */
export function ConfiguredSecretField({
  label,
  description,
  configured,
  masked,
  secret,
  onSecretChange,
  disabled = false,
  optional = false,
  vaultManaged = false,
  id,
}: ConfiguredSecretFieldProps) {
  const { t } = useTranslation('settings');
  const [reveal, setReveal] = useState(false);

  return (
    <FieldRow label={label} description={description} alignTop>
      <Stack spacing={1}>
        {configured !== undefined ? (
          <Chip
            size="small"
            variant="outlined"
            color={configured ? 'success' : 'default'}
            icon={configured ? <KeyRoundedIcon fontSize="small" /> : undefined}
            label={
              configured
                ? masked
                  ? t('configuredSecretField.setMaskedChip', { masked })
                  : t('configuredSecretField.setChip')
                : t('configuredSecretField.notSetChip')
            }
            sx={{ alignSelf: 'flex-start' }}
          />
        ) : null}
        <TextField
          id={id}
          size="small"
          fullWidth
          type={reveal ? 'text' : 'password'}
          value={secret}
          placeholder={
            optional
              ? t('configuredSecretField.optionalPlaceholder')
              : t('configuredSecretField.placeholder')
          }
          disabled={disabled}
          onChange={(event) => onSecretChange(event.target.value)}
          autoComplete="new-password"
          helperText={
            optional
              ? t('configuredSecretField.optionalHelperText')
              : t('configuredSecretField.helperText')
          }
          slotProps={{
            htmlInput: {
              'aria-label':
                typeof label === 'string' ? label : t('configuredSecretField.secretAriaFallback'),
            },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={
                      reveal
                        ? t('configuredSecretField.hideSecret')
                        : t('configuredSecretField.showSecret')
                    }
                    onClick={() => setReveal((value) => !value)}
                    disabled={disabled}
                  >
                    {reveal ? (
                      <VisibilityOffRoundedIcon fontSize="small" />
                    ) : (
                      <VisibilityRoundedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        {vaultManaged ? (
          <Chip
            size="small"
            variant="outlined"
            label={t('configuredSecretField.vaultManagedChip')}
            sx={{ alignSelf: 'flex-start' }}
          />
        ) : null}
      </Stack>
    </FieldRow>
  );
}
